import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { ArrowRightLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { createTransfer } from "../../services/crmServices/transferService";
import { getFullName } from "../../services/common/util";
import { useAuth } from "../../hooks/useAuth";
import moment from "moment-timezone";

export function AgentTransferModal({ open, onClose, onSuccess, lead }) {
    const { user } = useAuth();
    const [reason, setReason] = useState("");
    const [moneyInBank, setMoneyInBank] = useState("");
    const [loading, setLoading] = useState(false);

    const handleTransfer = async () => {
        if (!reason.trim()) {
            toast.error("Please provide a reason for the transfer");
            return;
        }

        setLoading(true);

        try {
            const transferData = {
                lead: lead._id,
                requestedReason: reason.trim(),
                requestedBy: user._id,
                requestedAt: moment().tz("America/New_York").toDate(),
                status: "pending",
                moneyInBank: moneyInBank ? parseFloat(moneyInBank) : 0
            };

            const response = await createTransfer(transferData);

            if (response?.status === 200) {
                toast.success(
                    "Transfer request submitted successfully! The lead has been sent to your manager for approval."
                );
                onSuccess();
            } else {
                toast.error(response?.data?.message || "Failed to submit transfer request");
            }
        } catch (error) {
            console.error("Transfer error:", error);
            toast.error("Failed to submit transfer request");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setReason("");
        setMoneyInBank("");
        onClose();
    };

    if (!lead) return null;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center">
                        <ArrowRightLeft className="h-5 w-5 mr-2" />
                        Transfer Lead
                    </DialogTitle>
                    <DialogDescription>
                        Request to transfer {getFullName(lead)} to another desk/agent.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium mb-2">Lead Details</h4>
                        <div className="text-sm space-y-1">
                            <p>
                                <strong>Name:</strong> {getFullName(lead)}
                            </p>
                            <p>
                                <strong>Email:</strong> {lead.email}
                            </p>
                            <p>
                                <strong>Status:</strong> {lead.status}
                            </p>
                            <p>
                                <strong>Current Desk:</strong> {lead.desk?.name || "No desk assigned"}
                            </p>
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="reason">Reason for Transfer *</Label>
                        <Textarea
                            id="reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Please explain why you want to transfer this lead..."
                            rows={4}
                            required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            This information will help your manager understand the transfer request.
                        </p>
                    </div>

                    <div>
                        <Label htmlFor="moneyInBank">Money in Bank (Optional)</Label>
                        <Input
                            id="moneyInBank"
                            type="number"
                            value={moneyInBank}
                            onChange={(e) => setMoneyInBank(e.target.value)}
                            placeholder="e.g., 5000"
                            min="0"
                            step="0.01"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            How much money does the client have in their account?
                        </p>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                        <div className="flex items-start">
                            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                            <div>
                                <h4 className="font-medium text-yellow-900 mb-2">Important Notes</h4>
                                <ul className="text-sm text-yellow-800 space-y-1">
                                    <li>• This lead will be removed from your leads list immediately</li>
                                    <li>• The transfer request will be sent to your manager for approval</li>
                                    <li>• You will be notified once the transfer is approved or rejected</li>
                                    <li>• If rejected, the lead will be returned to your leads list</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="flex space-x-3">
                        <Button
                            onClick={handleTransfer}
                            disabled={loading || !reason.trim()}
                            className="flex-1"
                        >
                            {loading ? "Submitting..." : "Submit Transfer Request"}
                        </Button>
                        <Button variant="outline" onClick={handleClose} disabled={loading} className="flex-1">
                            Cancel
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
